/**
 * SEO HEADING STRUCTURE - Flash Cards
 * ====================================
 * 
 * TITLE: Flash Cards | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Create and study with digital flash cards...
 * 
 * H1 (1 tag):
 *   - "Flash Cards" (hero section)
 * 
 * H2 (3 tags):
 *   - "Flashcard Features" (features section)
 *   - "Why Use Digital Flash Cards?" (benefits section)
 *   - "Ready to Start Learning?" (CTA section)
 * 
 * H3 (8 tags):
 *   - Feature card titles (4): "Card Decks", "Spaced Repetition", etc.
 *   - Benefit titles (4): Dynamic benefit items
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Layers, Repeat, BookOpen, Sparkles, ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const flashcardAlternatives = [
  { name: "Anki", path: "/flash-cards/csw-vs-anki/" },
  { name: "Brainscape", path: "/flash-cards/csw-vs-brainscape/" },
  { name: "Memrise", path: "/flash-cards/csw-vs-memrise/" },
  { name: "Cram", path: "/flash-cards/csw-vs-cram/" },
  { name: "NoteGPT", path: "/flash-cards/csw-vs-notegpt/" },
  { name: "Knowt", path: "/flash-cards/csw-vs-knowt/" },
  { name: "Kahoot!", path: "/flash-cards/csw-vs-kahoot/" },
  { name: "AnkiApp", path: "/flash-cards/csw-vs-ankiapp/" },
  { name: "RemNote", path: "/flash-cards/csw-vs-remnote/" },
  { name: "StudyBlue", path: "/flash-cards/csw-vs-studyblue/" },
  { name: "StudyStack", path: "/flash-cards/csw-vs-studystack/" },
  { name: "ProProfs", path: "/flash-cards/csw-vs-proprofs/" },
  { name: "Flashcard Machine", path: "/flash-cards/csw-vs-flashcard-machine/" },
  { name: "Memozora", path: "/flash-cards/csw-vs-memozora/" },
  { name: "iDoRecall", path: "/flash-cards/csw-vs-idorecall/" },
  { name: "StudyKit", path: "/flash-cards/csw-vs-studykit/" },
];

const features = [
  {
    icon: Layers,
    title: "Card Decks",
    description: "Organize cards into decks by subject, topic, or difficulty",
  },
  {
    icon: Repeat,
    title: "Spaced Repetition",
    description: "Review cards at optimal intervals for long-term retention",
  },
  {
    icon: BookOpen,
    title: "Quick Creation",
    description: "Add new cards instantly or import from notes and documents",
  },
  {
    icon: Sparkles,
    title: "AI Generation",
    description: "Let AI create flash cards from your study materials",
  },
];

const FlashCards = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Flash Cards - AI-Powered Spaced Repetition"
        description="Create flashcards for studying with spaced repetition. AI-powered card generation, organized decks, and long-term retention for effective learning."
        keywords="flashcards, flash card maker, spaced repetition, study cards, anki alternative"
        canonicalUrl="/flash-cards/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Master Any Subject</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Flash Cards</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  Master any subject with spaced repetition. Create, study, and memorize with intelligent flash cards.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Start Studying
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Create Deck
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">Spaced</p>
                    <p className="text-sm text-muted-foreground">Repetition</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">AI</p>
                    <p className="text-sm text-muted-foreground">Generation</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Cards Mastered</p>
                      <p className="text-xl font-bold text-foreground">127 cards</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Layers className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Smart{" "}
                <span className="text-gradient">Memorization</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Powerful features for effective learning
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl text-center"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Simple Study{" "}
                <span className="text-gradient">Flow</span>
              </h2>
            </div>
            
            <div className="space-y-8">
              {[
                { step: 1, title: "Create Your Deck", desc: "Add flash cards manually or use AI to generate them from your notes" },
                { step: 2, title: "Study & Flip", desc: "Review cards by flipping between question and answer sides" },
                { step: 3, title: "Rate Your Knowledge", desc: "Mark cards as easy, medium, or hard to optimize review schedule" },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex gap-6 items-start p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg group"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary text-primary-foreground flex items-center justify-center font-bold text-xl font-display group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 font-display">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Build Your First{" "}
              <span className="text-gradient">Deck</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Start memorizing smarter with CSW's flash cards today.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Create Flash Cards
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>

        {/* Compare Flashcard Tools Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-muted" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Compare{" "}
                <span className="text-gradient">Flashcard Tools</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                See how CSW compares to other popular flashcard platforms
              </p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
              {flashcardAlternatives.map((tool) => (
                <Link
                  key={tool.path}
                  to={tool.path}
                  className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg text-center"
                >
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    CSW vs {tool.name}
                  </span>
                  <ExternalLink className="w-3 h-3 inline-block ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FlashCards;